import React, { useState, useEffect } from 'react';
import { 
  X, FileText, Download, Package, MapPin, Truck, 
  Calendar, CheckCircle2, ShieldCheck, Loader2, AlertCircle, ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DocketDetailModal({ isOpen, onClose, docketId, onTrackClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && docketId) {
      fetchDetail();
    } else {
      setData(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docketId]);

  const fetchDetail = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/dockets/${docketId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to load shipment details');
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Error loading shipment details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (doc) => {
    if (!doc.url) return;
    window.open(doc.url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-100 overflow-hidden animate-scaleIn font-ibm">
        {/* Header */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600/30 border border-blue-500/30 rounded-xl flex items-center justify-center text-blue-400">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 block">
                Consignment Specification
              </span>
              <h2 className="text-lg font-bold font-manrope">
                #{data?.docket_number || 'Loading...'}
              </h2>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl h-8 w-8 p-0"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content Body */}
        <div className="p-6 max-h-[75vh] overflow-y-auto space-y-6">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500 gap-2">
              <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              <span className="text-xs">Fetching consignment details...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-3 text-xs">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          ) : data ? (
            <>
              {/* Route & Status Banner */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                    <span>{data.header?.origin}</span>
                    <ArrowRight className="w-4 h-4 text-blue-600" />
                    <span>{data.header?.destination}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                      {data.status?.replace(/_/g, ' ') || 'ACTIVE'}
                    </span>
                    <button
                      type="button"
                      onClick={() => { onClose(); onTrackClick && onTrackClick(data.id, data.docket_number); }}
                      className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Truck className="w-3.5 h-3.5" />
                      <span>Live Tracker</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Booking Date</span>
                    <span className="font-semibold text-slate-800">{data.header?.docket_date || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Mode</span>
                    <span className="font-semibold text-slate-800">{data.shipment_type || 'ROAD'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Packages</span>
                    <span className="font-semibold text-slate-800">{data.cargo?.packages || '1'} Pkgs</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Weight</span>
                    <span className="font-semibold text-slate-800">{data.cargo?.chargeable_weight || data.cargo?.gross_weight || '-'} Kg</span>
                  </div>
                </div>
              </div>

              {/* Shipper & Consignee */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border border-slate-200 rounded-xl p-4 bg-white">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                    Shipper / Consignor
                  </span>
                  <p className="text-sm font-bold text-slate-900 font-manrope">{data.header?.shipper_name || 'Shipper'}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{data.header?.shipper_address || 'Origin Address Registered'}</p>
                </div>

                <div className="border border-slate-200 rounded-xl p-4 bg-white">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                    Consignee / Receiver
                  </span>
                  <p className="text-sm font-bold text-slate-900 font-manrope">{data.header?.consignee_name || 'Consignee'}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{data.header?.consignee_address || 'Delivery Address Registered'}</p>
                </div>
              </div>

              {/* Cargo Details */}
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-2">
                  Cargo Specifications
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Package Packaging</span>
                    <span className="font-semibold text-slate-800">{data.cargo?.package_type}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Commodity / Nature</span>
                    <span className="font-semibold text-slate-800">{data.cargo?.nature_of_goods}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Dimensions</span>
                    <span className="font-semibold text-slate-800">{data.cargo?.dimensions}</span>
                  </div>
                </div>
              </div>

              {/* Client Documents */}
              {data.documents && data.documents.length > 0 && (
                <div>
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-wider block mb-2.5">
                    Official Documents &amp; e-POD
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {data.documents.map((doc, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-all"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-slate-800 block">{doc.name}</span>
                            <span className="text-[10px] text-slate-400 block">{doc.type} Format</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(doc)}
                          className="h-8 text-xs font-bold border-slate-300 text-slate-700 hover:text-blue-700 rounded-lg flex items-center gap-1"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 p-4 flex items-center justify-between">
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Verified Customer Record</span>
          </span>
          <Button
            onClick={onClose}
            className="bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs h-9 px-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
